import type { GuidedAnswers, GuidedAttestation, GuidedRaisedFlag } from './guided-reporting';

export type ClaimStatus = 'pending' | 'approved';

/**
 * The EI application wizard's full answer set (`EiApplicationForm.tsx`,
 * a 4-step flow since spec 014). Condensed from the real
 * Service Canada online application's ~25 screens -- see
 * EiApplicationForm.tsx's own comment for exactly which real screens
 * map to which group here, and which were deliberately dropped (identity
 * verification/temporary password, the Quebec Parental Insurance Plan
 * sub-flow, farming income, the variable-best-weeks manual
 * entry table, and the training-program sub-flow's detailed questions --
 * either redundant with this PoT's real PKCE sign-in, or genuine
 * edge-case branches not worth the complexity here).
 */
export interface EiApplicationInput {
  personal: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    addressLine1: string;
    city: string;
    province: string;
    postalCode: string;
    phone: string;
    preferredLanguage: 'en' | 'fr';
  };
  separation: {
    employerName: string;
    lastDayWorked: string;
    reasonCode: 'shortage_of_work' | 'dismissal' | 'quit' | 'other';
    payRate: number;
    payPeriod: 'hourly' | 'weekly' | 'biweekly' | 'monthly';
    jobTitle: string;
  };
  otherEmployment: {
    hadOtherEmployers: boolean;
    otherEmployerName?: string;
  };
  eligibility: {
    workersCompensation: boolean;
    pension: boolean;
    selfEmployedOrBusiness: boolean;
    inTrainingProgram: boolean;
    // Spec 013's "other situations" checklist -- stored flags only, no
    // eligibility determination branches on them yet. Optional: the BFF
    // stores applications verbatim, so claims stored before spec 013 (and
    // any older client's POST) don't carry them -- treat a missing flag as
    // unanswered, not as false.
    unionMember?: boolean;
    quebecParentalInsurance?: boolean;
    relatedToEmployer?: boolean;
    unableToWorkMedical?: boolean;
    incarcerated?: boolean;
    pregnantOrRecentBirth?: boolean;
  };
  availability: {
    availableImmediately: boolean;
    availableFromDate?: string;
    educationLevel: 'high_school' | 'college_trade' | 'undergraduate' | 'graduate' | 'other';
  };
  directDeposit: {
    enrolling: boolean;
    institutionNumber?: string;
    transitNumber?: string;
    accountNumber?: string;
  };
  // Spec 014's additions are optional for the same reason as the spec 013
  // flags above: claims stored before spec 014, and posts from a
  // still-loaded older frontend (which the BFF still accepts -- see
  // declarationAccepted below), don't carry them. The current wizard always
  // sends every one of them.
  //
  // Stored-only POC preferences (no downstream logic).
  taxSlipDelivery?: 'online' | 'mail';
  taxDeduction?: 'basic' | 'basic_and_spouse';
  // The three consents the review step requires; all three must be true to
  // submit.
  statementsConfirmed?: boolean;
  consentToShare?: boolean;
  rightsAccepted?: boolean;
  thirdPartyAssistance?: boolean;
  helperName?: string;
  /**
   * @deprecated The pre-spec-014 single declaration. Still accepted by
   * POST /api/v1/applications (treated as all three consents) so an
   * already-loaded older frontend can submit during a rollout; the current
   * wizard sends the three consents instead. Drop in a later release.
   */
  declarationAccepted?: boolean;
}

export interface EiClaim {
  id: string;
  applicantSub: string;
  status: ClaimStatus;
  weeklyBenefitAmount: number;
  appliedAt: string;
  application?: EiApplicationInput;
}

export interface EiReport {
  id: string;
  applicantSub: string;
  claimId: string;
  periodStart: string;
  periodEnd: string;
  /** For a guided report (spec 018), the sum of weeks 1 and 2. */
  workedHours: number;
  /** For a guided report (spec 018), the sum of weeks 1 and 2. */
  earnings: number;
  submittedAt: string;
  // Spec 018's guided report, additive: absent on reports filed through
  // spec 016's minimal confirm step.
  answers?: GuidedAnswers;
  /** The shape version `answers` were stored under (BFF's CONVERSATION_STATE_VERSION). */
  answersVersion?: number;
  flags?: GuidedRaisedFlag[];
  attestation?: GuidedAttestation;
}

export type EiReportingStatusLabel = 'not_yet_due' | 'due_soon' | 'overdue';

/**
 * Insurable totals from the ROE, surfaced read-only on the application
 * wizard's confirm step (docs/specs/012-ei-application-prefill.md). Both
 * null whenever epayroll-ube was unreachable -- the BFF always answers 200,
 * so "unavailable" is a null pair, never an error the caller must handle.
 *
 * Reference data for the citizen's awareness only: never part of
 * EiApplicationInput, never submitted back.
 */
export interface EiApplicationPreFill {
  insurableHours: number | null;
  insurableEarnings: number | null;
}

export interface EiReportingStatus {
  claimId: string;
  nextReportDue: string;
  daysUntilDue: number;
  status: EiReportingStatusLabel;
  /**
   * The two-week reporting schedule (docs/specs/016-ei-reporting-flow.md).
   * Additive and optional: the dashboard / life-events widgets read only the
   * four fields above.
   */
  periods?: EiReportingPeriod[];
}

export type EiReportingPeriodStatus = 'submitted' | 'due' | 'overdue' | 'upcoming';

/** One two-week reporting period, as the BFF's computePeriods returns it. */
export interface EiReportingPeriod {
  /** ISO timestamp; the period is [periodStart, periodEnd). */
  periodStart: string;
  periodEnd: string;
  /** Equals periodEnd. */
  dueDate: string;
  status: EiReportingPeriodStatus;
  /** Submitted periods only: 2 x the claim's weekly benefit, to be paid. */
  amount?: number;
  /** Submitted periods only. */
  submittedAt?: string;
}
